/* eslint-disable no-bitwise */
import { Accordion, styled } from '@mui/material';
import { theme } from 'components/Layout/ThemeProvider';
import { EFontFamily } from 'helpers/constants';
import { primary } from 'helpers/colors';
import { createUseStyles } from 'react-jss';

export const darkenHex = (hexColor: string, amount = 0.35): string => {
  const sanitizedHex = (hexColor ?? '').replace(/^#/, '');
  if (sanitizedHex.length !== 6) return '#333';

  const r = parseInt(sanitizedHex.substring(0, 2), 16) / 255;
  const g = parseInt(sanitizedHex.substring(2, 4), 16) / 255;
  const b = parseInt(sanitizedHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / delta + 6) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h /= 6;
  }

  l = Math.max(0, l - amount);

  const hue2rgb = (p: number, q: number, t: number) => {
    const tt = ((t % 1) + 1) % 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const toHex = (x: number) =>
    Math.round(hue2rgb(p, q, x) * 255)
      .toString(16)
      .padStart(2, '0');

  return `#${toHex(h + 1 / 3)}${toHex(h)}${toHex(h - 1 / 3)}`;
};

export const BadgeItem = styled(Accordion)(({ color }) => ({
  color: darkenHex(color as string),
  backgroundColor: `${color}`,
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: 'none',
  width: 210,
  padding: '5px 10px',
  fontSize: 12,
  fontWeight: 400,
  borderRadius: '15px !important',
  fontFamily: EFontFamily.inter
}));

export const StatusText = {
  width: 18,
  height: 18,
  backgroundColor: 'none',
  color: '#fff'
};

export const IconStyle = {
  width: 18,
  height: 18,
  color: '#919191'
};

export const CnpjNumber = {
  fontWeight: 'bold',
  color: '#54585a'
};

export const TicketNumber = {
  color: '#515253',
  fontWeight: 600
};

export const ClientName = {
  fontSize: 12,
  color: '#54585a'
};
export const ViewButton = {
  padding: '6px 10px 10px 10px',
  borderRadius: 8,
  border: '1px solid #c1c1c1'
};

export const useStyle = createUseStyles({
  datatableWrapper: {
    position: 'relative',
    '& [role="toolbar"]': {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      '& > div:first-child': {
        flex: 1,
        '& > div': {
          border: '1px solid #cacaca',
          borderRadius: 50,
          padding: '0 8px',
          width: '100%'
        },
        '& div::before': {
          display: 'none'
        },
        '& div::after': {
          display: 'none'
        }
      },
      '& > div:last-child': {
        flex: '0 0 auto',
        textAlign: 'initial'
      },
      '& button': {
        display: 'none'
      },
      '& .custom-toolbar-action button': {
        display: 'inline-flex'
      }
    },
    '& table': {
      fontSize: 14,
      lineHeight: '100%',
      border: 'none',
      borderRadius: 5,
      borderBottom: 'none',
      '& thead': {
        '& th': {
          color: '#fff',
          textTransform: 'uppercase',
          fontWeight: 400,
          backgroundColor: 'transparent',
          padding: '12px 16px',
          '&:first-child': {
            borderRadius: '10px 0px 0px 0px'
          },
          '&:last-child': {
            borderRadius: '0px 10px 0px 0px'
          },
          '& > div > span': {
            color: '#fff',
            padding: 0,
            '&[class*=checked], &[class*=indeterminate]': {
              color: '#fff'
            }
          }
        },
        '& button': {
          fontSize: 'inherit',
          padding: 'inherit',
          minWidth: 'unset',
          '& div, & svg': {
            color: '#fff !important'
          }
        }
      },
      '& tbody': {
        borderLeft: '#fdf5f5 thin solid',
        borderRight: '#fdf5f5 thin solid',
        '& tr': {
          borderBottom: '#f1f1f1 thin solid',
          '&.mui-row-selected': {
            backgroundColor: 'rgb(237 233 249)'
          },
          '&:hover td': { background: primary.lighter },
          '&:nth-child(even)': { background: '#faf9fe' }
        },
        '& td': {
          padding: 8,
          color: '#54585a',
          position: 'relative',
          '&:first-child': {
            textAlign: 'center',
            paddingLeft: 16
          },
          '& > div > span': {
            padding: 0,
            color: primary.muted,
            '&[class*=checked], &[class*=indeterminate]': {
              color: primary.muted
            }
          }
        }
      },
      '&.check-row': {
        '& tr': {
          cursor: 'pointer',
          '&.selected': {
            '& td': {
              backgroundColor: theme.palette.success.light,
              color: '#fff'
            }
          }
        }
      },
      '& .sign': {
        width: 10,
        height: 10,
        display: 'block',
        borderRadius: '50%',
        margin: 'auto',
        '&.active': { backgroundColor: theme.palette.success.main },
        '&.inactive': { backgroundColor: '#ccc' }
      }
    },
    '& > div > div': {
      paddingLeft: 0,
      paddingRight: 0
    },
    '& [class*="emptyTitle"]': {
      fontSize: 13,
      padding: '24px 0'
    },
    '& [class*="navContainer"] p': {
      fontSize: 12
    },
    '& [aria-label="Search"]': {
      fontSize: 13,
      minHeight: 25
    }
  }
});
